import React, { useCallback, useMemo, useState } from "react"
import { semesterKeys } from "keys/semesters"
import { HasWarningCell, PerformBatteryCell, PerformBatteryHeader, ProfileCell, WarningHeader } from '..'
import { CircularProgress, IconButton, SvgIcon } from "@mui/material"
import { ReactComponent as PrintIcon } from 'assets/icons/Print.svg';
import { useMutation } from "@tanstack/react-query";
import { printAssessmentFetch } from "store/middlewares/assessment.crud";
import { useSelector } from "react-redux";
import { BetaHeaderCell, tableHooks } from "components/Table";
import ratingList from "keys/rating";
import { features } from "keys";
import { permissions } from "config/ability";

const useBatteryColumns = () => {
    const [currentRowId, setCurrentRowId] = useState(null)
    const { brokenDisplay } = useSelector(state => state.general)
    const testTakerNameCell = tableHooks.useTestTakerNameCell()

    const { mutateAsync: print, isPending: printPending } = useMutation({
        mutationFn: printAssessmentFetch,
        onSettled: () => {
            setCurrentRowId(null)
        }
    })

    const printReport = useCallback(async ({ id }) => {
        setCurrentRowId(id)
        const response = await print({ id, blurred: brokenDisplay })
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
        // Open PDF in new tab
        // const newWindow = window.open(url, '_blank');
        // Optionally, trigger print automatically (works in some browsers)
        // if (newWindow) {
        //     newWindow.onload = function () {
        //         newWindow.print();
        //     };
        // }
        // window.open(url, '_blank');
    }, [brokenDisplay])

    const columns = useMemo(
        () => [
            {
                accessorKey: 'id',
                header: "ID",
                showBlurred: true
            },
            {
                accessorKey: 'test_taker_affiliation_name',
                header: "שם בית ספר",
                showBlurred: true,
            },
            testTakerNameCell,
            {
                accessorKey: 'learning_unit_name',
                header: "מסגרת לימודית",
                enableSorting: false,
                showBlurred: true,
                showBrokenDisplay: true
            },
            {
                accessorKey: 'warning',
                header: <WarningHeader label="התראה" tooltip="חישוב לפי ממוצע מספר מילים נכונות לדקה" />,
                cell: HasWarningCell,
                showBlurred: true,
                meta: {
                    label: "התראה"
                }
            },
            {
                accessorKey: 'wcpm_fluency_avg_rating_index',
                header: <WarningHeader label="דירוג הקריאה" tooltip="פרופיל של שטף קריאה קולית" />,
                cell: (info) =>
                    <div style={{ color: ratingList?.[info.getValue() - 1] }}>
                        {info.getValue() || ""}
                    </div>,
                meta: {
                    label: "דירוג הקריאה"
                }
            },
            {
                accessorKey: 'reading_quality',
                header: () =>
                    <BetaHeaderCell>
                        <WarningHeader label="איכות הקריאה" tooltip="איכות הקריאה משלבת דיוק וקצב כדי לספק תמונה מלאה יותר של יכולת הקריאה." />
                    </BetaHeaderCell>,
                meta: {
                    label: "איכות הקריאה"
                },
                feature: features.reading_quality_cell,
                role: permissions.read
            },
            {
                accessorKey: 'profile',
                header: () => <BetaHeaderCell>{"פרופיל כללי"}</BetaHeaderCell>,
                cell: ProfileCell,
                enableSorting: false,
                showBlurred: true,
                meta: {
                    label: 'פרופיל כללי'
                }
            },
            {
                accessorKey: 'grade_of_execution_name',
                header: "שכבת ביצוע",
                showBlurred: true,
            },
            {
                accessorKey: 'battery_intended_for_name',
                header: "מועד לביצוע",
                cell: ({ row }) => {
                    const { semester_of_execution: semester, school_year: year } = row.original
                    return `${semesterKeys[semester]?.label || ""} ${year ? `${year - 1}-${year}` : ""}` || "לא ידוע"
                },
                enableSorting: false,
                showBlurred: true,
            },
            {
                accessorKey: 'battery_label',
                header: "שם הסוללה",
                showBlurred: true
            },
            {
                accessorKey: 'battery_performance',
                header: PerformBatteryHeader,
                cell: PerformBatteryCell,
                meta: {
                    label: "ביצוע"
                },
                showBlurred: true,
                enableSorting: false,
                feature: "battery-performance-cell",
                role: "read",
                permissionHOC: "hidden",
            },

            {
                accessorKey: 'print_report',
                header: "הדפס דוח",
                showBlurred: true,
                enableSorting: false,
                cell: ({ row }) => {
                    return (
                        <IconButton
                            disabled={row.original.battery_hidden}
                            onClick={() => printReport(row.original)}
                            label="Edit"
                            className="textPrimary"
                        >
                            {printPending && row.original.id == currentRowId ?
                                <CircularProgress size={20} color="thirdly" /> :
                                <SvgIcon component={PrintIcon} inheritViewBox />
                            }
                        </IconButton>
                    );
                }
            }
        ], [printPending, currentRowId, brokenDisplay]
    )

    return columns
}

export default useBatteryColumns